import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Loader2, MailCheck, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { logger } from '@/lib/logger';

// How long to wait for detectSessionInUrl to absorb the confirmation hash
// before declaring the link invalid. Matches ResetPasswordPage.
const SESSION_WAIT_MS = 3000;

const RESEND_COOLDOWN_MS = 30_000;

// A freshly confirmed user has an empty user_profiles row (the backend creates
// it lazily), so send them through onboarding to choose a role and fill it in
// rather than into an empty CRM.
const POST_CONFIRM_ROUTE = '/onboarding';

export function AuthConfirmPage() {
  const navigate = useNavigate();
  const { resendConfirmation } = useAuth();

  // null = still waiting for the confirmation session to appear
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(false);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let active = true;

    // detectSessionInUrl may have absorbed the confirmation hash before this
    // component mounted, so check for an existing session first…
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (active && session) setHasSession(true);
    });

    // …and also subscribe for the SIGNED_IN event in case the hash is
    // processed after mount.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === 'SIGNED_IN' || session) setHasSession(true);
    });

    const timer = setTimeout(() => {
      if (active) setHasSession((prev) => (prev === null ? false : prev));
    }, SESSION_WAIT_MS);

    return () => {
      active = false;
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  // Confirmed: the emailed link established a session, so continue into the app.
  useEffect(() => {
    if (hasSession) navigate(POST_CONFIRM_ROUTE, { replace: true });
  }, [hasSession, navigate]);

  useEffect(() => {
    return () => {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    };
  }, []);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendError(null);
    setIsResending(true);

    const result = await resendConfirmation(email);

    setIsResending(false);

    if (!result.success) {
      logger.error('resendConfirmation failed', result.error, { component: 'AuthConfirmPage' });
      setResendError('Could not send a new link. Please try again.');
      return;
    }

    setResent(true);
    setCooldown(true);
    cooldownTimer.current = setTimeout(() => setCooldown(false), RESEND_COOLDOWN_MS);
  };

  // Still waiting for the confirmation session, or about to redirect
  if (hasSession !== false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground text-sm">Confirming your account…</p>
        </div>
      </div>
    );
  }

  // Landed with no session: the confirmation link is expired or invalid
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">

        <Card className="backdrop-blur-xl shadow-2xl rounded-3xl">
          <CardContent className="p-8 space-y-4">
            <div className="h-16 w-16 mx-auto rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <div className="text-center space-y-2">
              <h1 className="text-xl font-semibold">Link expired or invalid</h1>
              <p className="text-sm text-muted-foreground">
                This confirmation link is no longer valid. Enter your email and
                we&apos;ll send you a new one.
              </p>
            </div>

            <form onSubmit={handleResend} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="w-5 h-5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="pl-11 h-12"
                  />
                </div>
              </div>

              {resent && (
                <Alert>
                  <MailCheck className="h-4 w-4" />
                  <AlertDescription>
                    If that account still needs confirming, we&apos;ve sent a new
                    link. Check your inbox (and spam folder).
                  </AlertDescription>
                </Alert>
              )}

              {resendError && (
                <Alert variant="destructive">
                  <AlertDescription className="text-center">{resendError}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={isResending || cooldown || !email}
                className="w-full h-12 text-base"
              >
                {isResending ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : cooldown ? (
                  'Link Sent — wait to resend'
                ) : resent ? (
                  'Resend Link'
                ) : (
                  'Send New Link'
                )}
              </Button>
            </form>

            <div className="text-center pt-2">
              <Link
                to="/login"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                Back to sign in
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none opacity-50" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl pointer-events-none opacity-30" />
      </div>
    </div>
  );
}

export default AuthConfirmPage;
